/**
 * Datei-Puffer der Immobilien-Erfassung — die Antwort auf die Reihenfolge-Falle.
 *
 * Beim **Neuanlegen** existiert das RiskObject zum Zeitpunkt der Dateiauswahl
 * noch nicht: es entsteht erst im Submit über `POST /risk-objects/bulk`. Ein
 * Temp-Dokument mit späterem Rehoming gibt es im Repo nicht; das etablierte
 * Muster ist „Files im Client parken → nach dem Anlegen mit der frischen Id
 * hochladen" (`PublicSchadenmeldungBig.tsx`). Genau das macht der Modus
 * `buffer`.
 *
 * Drei Modi, **explizit** über die Prop `uploads` hereingereicht — bewusst
 * **nicht** aus `chrome` abgeleitet: `RisikoanalyseDetailFormPublic` rendert
 * dieselbe Step-Form mit `chrome="embedded"` auf einer **öffentlichen** Seite
 * ohne Session; eine Ableitung aus `chrome` löste dort 401-Uploads aus.
 *
 * - `disabled` — kein Upload, nur der Hinweistext (öffentliche Wege).
 * - `buffer`   — Neuanlage: `File`-Objekte parken, Upload nach dem Speichern.
 * - `immediate`— Edit-Modus: `riskObjectId` bekannt, Upload sofort.
 * - `token`    — Kundenlink einer Immobilie: wie `immediate`, aber ohne
 *                Anmeldung über `public-form/:token/uploads` (10/2026).
 *
 * **Schlüssel-Design.** Puffer-Schlüssel ist `slotId({scope,key})` und enthält
 * die Gebäude-UUID; der Document-**Tag** ist `slotTag(key)` und enthält sie
 * bewusst **nicht**: die UUID wird bei jedem Mount neu erzeugt und wäre im
 * Edit-Modus wertlos. Ein RiskObject = ein Gebäude, der scope ist im Tag also
 * redundant.
 *
 * **Der Puffer überlebt kein Unmount** — `File`-Objekte sind nicht
 * serialisierbar. Fenster schließen verwirft vorgemerkte Dateien, deshalb
 * meldet die Step-Form vorgemerkte Dateien an das Dirty-Tracking aus #1307.
 */
import * as React from "react";
import { documentService, type DocumentDto } from "../../services/documentService";
import { kundenlinkUploads } from "./kundenlinkUploads";

// ── Modell ────────────────────────────────────────────────────────────

/** `"global"` (Objektebene) oder die Gebäude-UUID. */
export type UploadScope = string;

export const GLOBAL_SCOPE: UploadScope = "global";

export type PendingStatus = "pending" | "uploading" | "done" | "error";

export type PendingUpload = {
  /** Eindeutig je vorgemerkter Datei (nicht je Slot). */
  id: string;
  scope: UploadScope;
  /** Slot-Schlüssel ohne Gebäude-UUID, z. B. `unterlagen:grundriss`. */
  key: string;
  /** Für Fortschritts- und Fehlermeldungen. */
  label: string;
  file: File;
  status: PendingStatus;
  error?: string;
  documentId?: number;
};

export type UploadMode =
  | { mode: "disabled" }
  | { mode: "buffer" }
  | { mode: "immediate"; riskObjectId: number; contactId?: number | null }
  | { mode: "token"; token: string };

export const UPLOAD_DISABLED: UploadMode = { mode: "disabled" };

// ── Grenzen (Client-Vorprüfung) ───────────────────────────────────────

/**
 * Spiegel von `DOCUMENT_FILE_SIZE_LIMIT_BYTES` (Backend-Default 15 MiB,
 * `apps/api/src/document.controller.ts`). Die Prüfung hier ersetzt die
 * serverseitige **nicht** — sie erspart nur den vergeblichen Upload.
 */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/** Obergrenze je Slot — hält den Rate-Limit-Bucket (pro IP!) beherrschbar. */
export const MAX_FILES_PER_SLOT = 10;

/**
 * Spiegel von `BLOCKED_DOCUMENT_EXTENSIONS`. Das Backend prüft die **Endung**,
 * nicht den MIME-Type — deshalb hier genauso. Die Prüfung muss zwingend in JS
 * laufen: das `accept`-Attribut wird von Drag-and-drop und der Auswahl „Alle
 * Dateien" umgangen.
 */
export const BLOCKED_UPLOAD_EXTENSIONS = new Set([
  "exe", "msi", "com", "scr", "pif", "cpl", "dll", "apk", "app",
  "bat", "cmd", "vbs", "vbe", "js", "jse", "wsf", "wsh", "ps1", "psm1", "sh",
  "hta", "jar", "lnk", "reg",
]);

export const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot >= 0 && dot < fileName.length - 1 ? fileName.slice(dot + 1).toLowerCase() : "";
};

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Eine Datei gegen die Client-Grenzen prüfen. Rückgabe `null` = angenommen,
 * sonst der Text für die Inline-Meldung am Feld.
 */
export const pruefeDatei = (file: File, bereitsImSlot: number): string | null => {
  if (bereitsImSlot >= MAX_FILES_PER_SLOT) {
    return `Höchstens ${MAX_FILES_PER_SLOT} Dateien je Feld.`;
  }
  const ext = extensionOf(file.name);
  if (BLOCKED_UPLOAD_EXTENSIONS.has(ext)) {
    return `„${file.name}“: Ausführbare Dateien und Skripte sind nicht erlaubt.`;
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `„${file.name}“ ist ${formatBytes(file.size)} groß — erlaubt sind höchstens ${formatBytes(MAX_UPLOAD_BYTES)}.`;
  }
  if (file.size === 0) {
    return `„${file.name}“ ist leer.`;
  }
  return null;
};

// ── Schlüssel ─────────────────────────────────────────────────────────

/** Puffer-Schlüssel: **mit** Gebäude-Scope, damit zwei Gebäude sich nicht mischen. */
export const slotId = (scope: UploadScope, key: string): string => `${scope}::${key}`;

/**
 * Document-Tag: **ohne** Gebäude-Scope. Ein RiskObject ist genau ein Gebäude,
 * und die im Client erzeugte UUID gäbe es beim nächsten Öffnen nicht mehr.
 */
export const slotTag = (key: string): string => `immo-slot:${key}`;

/** Slot-Schlüssel der Mietvertrags-Ablage einer Einheit (index-basiert). */
export const einheitSlotKey = (idx: number): string => `einheit:${idx}:mietvertrag`;

const EINHEIT_KEY_RE = /^einheit:(\d+):(.+)$/;

/**
 * Index-Verschiebung nach dem Löschen einer Einheit. `Einheit` ist ein
 * `Record<string,string>` ohne stabile Id — ein künstliches `_uid` schlüge in
 * die JSON-serialisierten characteristics durch. Also wird umgeschrieben:
 * der Slot der gelöschten Einheit fällt weg, alle dahinter rücken auf.
 */
export const remapEinheitenKeys = <T extends { scope: UploadScope; key: string }>(
  eintraege: T[],
  scope: UploadScope,
  removedIdx: number,
): T[] => {
  const out: T[] = [];
  for (const e of eintraege) {
    const m = e.scope === scope ? EINHEIT_KEY_RE.exec(e.key) : null;
    if (!m) {
      out.push(e);
      continue;
    }
    const idx = Number(m[1]);
    if (idx === removedIdx) continue; // Datei der gelöschten Einheit entfällt
    out.push(idx > removedIdx ? { ...e, key: `einheit:${idx - 1}:${m[2]}` } : e);
  }
  return out;
};

// ── Context ───────────────────────────────────────────────────────────

export type UploadBufferValue = {
  mode: UploadMode;
  /** Alle vorgemerkten Dateien (Modus `buffer`). */
  pending: PendingUpload[];
  /** Vorgemerkte Dateien eines Slots. */
  filesFor: (scope: UploadScope, key: string) => PendingUpload[];
  /** Bereits am RiskObject hängende Dokumente eines Slots (Modus `immediate`). */
  docsFor: (scope: UploadScope, key: string) => DocumentDto[];
  /**
   * Dateien annehmen. Liefert die abgelehnten Dateien als Meldungstexte —
   * der Aufrufer zeigt sie am Feld an.
   */
  add: (scope: UploadScope, key: string, label: string, files: File[]) => string[];
  /** Vorgemerkte Datei wieder entfernen. */
  remove: (id: string) => void;
  /** Bereits hochgeladenes Dokument löschen (Modus `immediate`). */
  removeDocument: (documentId: number) => void;
  /**
   * Alle vorgemerkten Dateien eines Gebäudes verwerfen — beim Löschen des
   * Gebäudes. Ohne das blieben die Dateien im Puffer, fänden im Nachlauf kein
   * Ziel mehr und lägen am Ende am **falschen** Gebäude.
   */
  dropScope: (scope: UploadScope) => void;
  /**
   * Alle vorgemerkten Dateien EINES Slots verwerfen — wenn das Feld
   * verschwindet, weil seine Frage zurückgenommen wurde (Anlage abgewählt,
   * „Liegen Unterlagen vor?" auf „nein").
   *
   * Ohne diesen Abwurf bliebe die Datei im Puffer, `allPending()` reichte sie
   * an den Nachlauf durch und sie landete nach dem Anlegen als Dokument mit
   * dem Tag `immo-slot:<slot>` am Risikoobjekt — zu einer Anlage, die es dort
   * gar nicht gibt. Dieselbe Aufräumpflicht wie `dropScope` beim Löschen eines
   * Gebäudes und `remapEinheitenSlots` beim Löschen einer Einheit.
   *
   * Betrifft ausdrücklich NUR den Puffer: bereits hochgeladene Dokumente
   * (Modus `immediate`) bleiben unangetastet — sie zu löschen wäre eine
   * unwiderrufliche Nebenwirkung eines Klicks auf eine Kachel.
   */
  dropSlot: (scope: UploadScope, key: string) => void;
  /** Nach dem Löschen einer Einheit die index-basierten Slots nachziehen. */
  remapEinheitenSlots: (scope: UploadScope, removedIdx: number) => void;
  /**
   * Alle vorgemerkten Dateien — **über eine Ref**, damit der Submit-Handler
   * keinen stale closure liest.
   */
  allPending: () => PendingUpload[];
  /** Läuft gerade ein Sofort-Upload oder ein Nachladen? */
  busy: boolean;
  /** Letzter Fehler eines Sofort-Uploads (Modus `immediate`). */
  fehler: string | null;
};

const leererWert: UploadBufferValue = {
  mode: UPLOAD_DISABLED,
  pending: [],
  filesFor: () => [],
  docsFor: () => [],
  add: () => [],
  remove: () => {},
  removeDocument: () => {},
  dropScope: () => {},
  dropSlot: () => {},
  remapEinheitenSlots: () => {},
  allPending: () => [],
  busy: false,
  fehler: null,
};

const UploadBufferContext = React.createContext<UploadBufferValue>(leererWert);

/**
 * Ohne Provider (z. B. in einem isolierten Komponententest) fällt das Feld auf
 * `disabled` zurück und zeigt den Hinweistext — kein Absturz.
 */
export const useUploadBuffer = (): UploadBufferValue => React.useContext(UploadBufferContext);

let laufendeId = 0;
const naechsteId = (): string => `up-${Date.now().toString(36)}-${(laufendeId += 1)}`;

/**
 * Der State lebt **in der Step-Form**, nicht im Provider: der Submit-Handler
 * der Form braucht `allPending()`, könnte den eigenen Context aber nicht per
 * Hook lesen. Deshalb dieser Hook + ein dünner Provider darum.
 */
export const useUploadBufferState = (mode: UploadMode): UploadBufferValue => {
  const [pending, setPending] = React.useState<PendingUpload[]>([]);
  const [docs, setDocs] = React.useState<DocumentDto[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const pendingRef = React.useRef<PendingUpload[]>([]);
  pendingRef.current = pending;
  /** Sequenzierung der Sofort-Uploads über alle `add`-Aufrufe hinweg. */
  const warteschlange = React.useRef<Promise<void>>(Promise.resolve());
  const offeneLaeufe = React.useRef(0);

  const riskObjectId = mode.mode === "immediate" ? mode.riskObjectId : null;
  const contactId = mode.mode === "immediate" ? (mode.contactId ?? null) : null;
  const token = mode.mode === "token" ? mode.token : null;

  // Sofort-Upload über die Sitzung (`immediate`) oder den Kundenlink (`token`)
  // — dieselben drei Operationen, nur ein anderer Transport.
  const transport = React.useMemo(() => {
    if (token) {
      const t = kundenlinkUploads(token);
      return {
        list: () => t.list() as Promise<DocumentDto[]>,
        upload: (file: File, key: string) => t.upload(file, key) as Promise<DocumentDto>,
        remove: (id: number) => t.remove(id),
      };
    }
    if (riskObjectId == null) return null;
    return {
      list: () => documentService.list({ riskObjectId }),
      upload: (file: File, key: string) =>
        documentService.upload({
          file,
          riskObjectId,
          contactId: contactId ?? undefined,
          tags: [slotTag(key)],
        }),
      remove: (id: number) => documentService.remove(id),
    };
  }, [token, riskObjectId, contactId]);

  // Sofort-Modi: die bereits am Risikoobjekt hängenden Dateien holen und über
  // den Tag den Slots zuordnen.
  const nachladen = React.useCallback(() => {
    if (!transport) return;
    let abgebrochen = false;
    transport
      .list()
      .then((liste) => {
        if (!abgebrochen) setDocs(liste);
      })
      .catch(() => {
        if (!abgebrochen) setDocs([]);
      });
    return () => {
      abgebrochen = true;
    };
  }, [transport]);

  React.useEffect(() => {
    if (!transport) {
      setDocs([]);
      return;
    }
    return nachladen();
  }, [transport, nachladen]);

  const filesFor = React.useCallback(
    (scope: UploadScope, key: string) =>
      pending.filter((p) => p.scope === scope && p.key === key),
    [pending],
  );

  const docsFor = React.useCallback(
    (_scope: UploadScope, key: string) => {
      const tag = slotTag(key);
      return docs.filter((d) => (d.tags ?? []).includes(tag));
    },
    [docs],
  );

  const dropScope = React.useCallback((scope: UploadScope) => {
    setPending((prev) => prev.filter((p) => p.scope !== scope));
  }, []);

  const dropSlot = React.useCallback((scope: UploadScope, key: string) => {
    setPending((prev) => prev.filter((p) => !(p.scope === scope && p.key === key)));
  }, []);

  const remove = React.useCallback((id: string) => {
    setPending((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const removeDocument = React.useCallback(
    (documentId: number) => {
      if (!transport) return;
      // Über den Kundenlink darf nur gelöscht werden, was der Kunde selbst
      // hochgeladen hat — Dateien des Beraters bleiben stehen.
      const doc = docs.find((d) => d.id === documentId) as (DocumentDto & { eigene?: boolean }) | undefined;
      if (token && doc?.eigene === false) {
        setFehler("Diese Datei hat Ihr Berater hinterlegt — sie kann hier nicht gelöscht werden.");
        return;
      }
      setBusy(true);
      setFehler(null);
      transport
        .remove(documentId)
        .then(() => setDocs((prev) => prev.filter((d) => d.id !== documentId)))
        .catch((err) =>
          setFehler(err instanceof Error ? err.message : "Die Datei konnte nicht gelöscht werden."),
        )
        .finally(() => setBusy(false));
    },
    [transport, docs, token],
  );

  const add = React.useCallback<UploadBufferValue["add"]>(
    (scope, key, label, files) => {
      if (mode.mode === "disabled") return [];
      const abgelehnt: string[] = [];
      const angenommen: PendingUpload[] = [];
      // Bestand des Slots: vorgemerkt + bereits hochgeladen.
      const tag = slotTag(key);
      let belegt =
        pendingRef.current.filter((p) => p.scope === scope && p.key === key).length +
        docs.filter((d) => (d.tags ?? []).includes(tag)).length;
      for (const file of files) {
        const meldung = pruefeDatei(file, belegt);
        if (meldung) {
          abgelehnt.push(meldung);
          continue;
        }
        belegt += 1;
        angenommen.push({
          id: naechsteId(),
          scope,
          key,
          label,
          file,
          status: "pending",
        });
      }
      if (angenommen.length === 0) return abgelehnt;

      if (mode.mode === "buffer") {
        setPending((prev) => [...prev, ...angenommen]);
        return abgelehnt;
      }
      if (!transport) return abgelehnt;

      // Modus `immediate`: sofort hochladen, streng sequenziell (der
      // Rate-Limit-Bucket des Upload-Endpunkts gilt pro IP und wird hinter dem
      // zentralen Proxy von allen Nutzern geteilt). Sequenziell heißt hier
      // auch: über ALLE `add`-Aufrufe hinweg. Zwei schnell aufeinander
      // folgende Drops hängen sich deshalb an dieselbe Warteschlange, statt je
      // eine eigene Schleife zu starten.
      setPending((prev) => [...prev, ...angenommen.map((p) => ({ ...p, status: "uploading" as const }))]);
      offeneLaeufe.current += 1;
      setBusy(true);
      setFehler(null);
      const lauf = async () => {
        for (const p of angenommen) {
          try {
            const doc = await transport.upload(p.file, p.key);
            setDocs((prev) => [doc, ...prev]);
            setPending((prev) => prev.filter((x) => x.id !== p.id));
          } catch (err) {
            const text = err instanceof Error ? err.message : "Upload fehlgeschlagen";
            setPending((prev) =>
              prev.map((x) => (x.id === p.id ? { ...x, status: "error" as const, error: text } : x)),
            );
            setFehler(text);
          }
        }
      };
      // `catch` vor und nach dem Lauf: ein einzelner Fehlschlag darf die Kette
      // nicht vergiften, sonst liefe kein weiterer Upload mehr an.
      warteschlange.current = warteschlange.current
        .catch(() => {})
        .then(lauf)
        .catch(() => {})
        .then(() => {
          offeneLaeufe.current -= 1;
          // Erst wenn KEIN Lauf mehr aussteht — sonst meldete der zuerst
          // fertige Lauf „nicht mehr beschäftigt", während noch einer läuft.
          if (offeneLaeufe.current <= 0) setBusy(false);
        });
      return abgelehnt;
    },
    [mode, docs, transport],
  );

  const remapEinheitenSlots = React.useCallback((scope: UploadScope, removedIdx: number) => {
    setPending((prev) => remapEinheitenKeys(prev, scope, removedIdx));
  }, []);

  const allPending = React.useCallback(() => pendingRef.current, []);

  return React.useMemo<UploadBufferValue>(
    () => ({
      mode,
      pending,
      filesFor,
      docsFor,
      add,
      remove,
      removeDocument,
      dropScope,
      dropSlot,
      remapEinheitenSlots,
      allPending,
      busy,
      fehler,
    }),
    [
      mode,
      pending,
      filesFor,
      docsFor,
      add,
      remove,
      removeDocument,
      dropScope,
      dropSlot,
      remapEinheitenSlots,
      allPending,
      busy,
      fehler,
    ],
  );
};

export const UploadBufferProvider = ({
  value,
  children,
}: {
  value: UploadBufferValue;
  children: React.ReactNode;
}) => <UploadBufferContext.Provider value={value}>{children}</UploadBufferContext.Provider>;

export default UploadBufferProvider;
